import React, { Fragment } from "react"

import { Grid } from "@mui/material"

import { getEnvVar, isEnvVariableTruthy } from "../../../utils/genericHelpers"
import type { InsightProps } from "../interfaces"
import { DistributionCurveContainer } from "./DistributionCurveInsight"
import { FeaturesBreakdownContainer } from "./FeaturesBreakdownInsight"
import { TopFeaturesGraphContainer } from "./TopFeaturesInsights"

/**
 * FeaturesCountsDashboardInsight Component
 *
 * Comprehensive dashboard component that combines multiple insight visualizations
 * to provide a complete view of feature analysis.
 *
 * Components included:
 * - TopFeaturesGraph: Shows most influential features
 * - DistributionCurve: Displays numerical feature distributions
 * - FeaturesBreakdown: Presents categorical feature analysis
 *
 * Layout:
 * - Organized in a vertical flow for optimal readability
 * - Each component maintains independent state
 * - Consistent styling and interaction patterns
 *
 * @param {InsightProps} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Fragment containing the complete dashboard layout
 */
export const FeaturesCountsDashboardInsight = (props: InsightProps): React.ReactElement => {
  const { insight } = props

  const isDistributionInsightEnabled = isEnvVariableTruthy(String(getEnvVar("FEATURE_DISTRIBUTION_INSIGHT_ENABLED")))
  const isTopFeaturesInsightEnabled = isEnvVariableTruthy(String(getEnvVar("TOP_FEATURES_INSIGHT_ENABLED")))
  const isFeaturesBreakdownInsightEnabled = isEnvVariableTruthy(String(getEnvVar("FEATURES_BREAKDOWN_INSIGHT_ENABLED")))

  return (
    <Fragment>
      {isTopFeaturesInsightEnabled && (
        <Grid item xs={12}>
          <TopFeaturesGraphContainer insight={insight} />
        </Grid>
      )}

      {isDistributionInsightEnabled && (
        <Grid item xs={12}>
          <DistributionCurveContainer insight={insight} />
        </Grid>
      )}

      {isFeaturesBreakdownInsightEnabled && (
        <Grid item xs={12}>
          <FeaturesBreakdownContainer insight={insight} />
        </Grid>
      )}
    </Fragment>
  )
}
